import _throttle from "lodash/throttle";
import _isElement from "lodash/isElement";
import _isNumber from "lodash/isNumber";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import traversal from "web/script/dom/traversal";

const name = "buybuybuy-sticky-buy-button";

const selectors = {
    BUY_OPTIONS: ".buybuybuy-options",
    OVERLAY: ".base-overlay",
};

const states = {
    FADE: "buybuybuy-area__sticky-buy-button--fade",
};

/**
 * When scrolling the viewport to the Buy Options in `this._scrollToBuyOptions`, we want the
 * size picker to always be visible. This ratio is added to the algorithm computing the new scroll
 * position to ensure that.
 *
 * @see this._scrollToBuyOptions
 * @type {number}
 */
const sizePickerOffset = 30;

/**
 * NEW PDP SITCKY BUY BUTTON
 *
 * A Sticky Button that shows when any `Add to Bag` CTA is not visible. When clicked, it scrolls
 * the users to the Buy Options so that he can add to bag.
 *
 * @TODO Needs to be refined to integrate better with componeonts/mobile-header
 *
 * @see components/buybuybuy-area
 * @see components/desktop-header
 * @see componeonts/mobile-header
 * @see components/app-header
 */
export default baustein.register(name, {
    /**
     * The last cached scroll top value.
     *
     * @see _toggleOnScroll()
     * @type {number}
     */
    lastScrollTop: 0,

    /**
     * The computed height for the Header in the page. Defaults to 0. This component is ignorant
     * of the presence of a Header in the page, however a reference to that height can be set via
     * `this.setHeaderHeightReference(height)`.
     *
     * @see this.setHeaderHeightReference
     * @type {number}
     */
    headerHeight: 0,

    /**
     * The computed offset for the Header . `app header` and `desktop-header` expand/collapse
     * when you scroll. We need to take in account to compute accurately the actual visible part
     * of the header in the screen.
     *
     * @see updatePosition()
     * @type {number}
     */
    headerOffset: 0,

    /**
     * The currently registered scroll handler by this.hideWhenIsVisible().
     * We need to chache this in order to be able to update the scroll handler when the Sticky
     * Buy Button visible target changes.
     *
     * @see this.hideWhenIsVisible();
     * @type {Function | null}
     */
    registeredScrollHandler: null,

    setupEvents(add) {
        add("click", this._scrollToBuyOptions);
    },

    onInsert() {
        // not support for overlays yet
        if (traversal.closest(this.el, selectors.OVERLAY)) {
            this.destroy();
            return;
        }

        requestAnimationFrame(() => {
            // white-space between the header and the button
            const margin = 20;

            this.el.style.top = `${this.headerHeight + margin}px`;
            this.el.classList.remove("hidden");
        });
    },

    /**
     * If supplied a number greater than 0, sets the component reference to the Header's height.
     * Consumed by components/lyst-app onInsert().
     *
     * @public
     * @see this.headerHeight
     * @param {number} h The computed height for the Header element in the page.
     * @returns {undefined}
     */
    setHeaderHeightReference(h) {
        if (_isNumber(h) && h > 0) {
            this.headerHeight = h;
        }
    },

    /**
     * Make sure  the Y axis translation of Sticky Buy Button  **always** matches the Y axis translation
     * of components/app-header or components/deskstop-header.
     *
     * @public
     * @see components/lyst-app onAppHeaderUpdatedPosition
     * @see components/app-header _onScroll
     * @param {number} offset
     * @returns {undefined}
     */
    updatePosition(offset) {
        this.headerOffset = offset;

        requestAnimationFrame(() => {
            this.el.style.transform = `translateY(-${offset}px)`;
        });
    },

    /**
     * Registers a scroll handler that will call _toggleOnScroll with the supplied targets.
     * This will efectively hide the component if any targets are visible within the viewport and
     * not overlapped by the page header.
     *
     * @public
     * @see this._toggleOnScroll()
     * @param {Array<HTMLElement>} targets
     * @returns {undefined}
     */
    hideWhenIsVisible(targets) {
        const targetElements = targets.filter((target) => _isElement(target));

        if (!targetElements.length) {
            return;
        }

        if (this.registeredScrollHandler) {
            this.releaseGlobalHandler("scroll", this.registeredScrollHandler);
        }

        this.registeredScrollHandler = _throttle(
            requestAnimationFrame.bind(window, this._toggleOnScroll.bind(this, targetElements)),
            100
        );

        this.setGlobalHandler("scroll", this.registeredScrollHandler);
        requestAnimationFrame(() => this._toggleOnScroll(targetElements));
    },

    /**
     * Make sure that, whenever a supplied target HTMLElement is visible within the viewport, then
     * the Sticky Buy Button is not. Acknowledge that a target HTMLElement is not visible while it's
     * overlapped by the Header.
     *
     * @TODO This method encapsulates the more complicated logic of this component and probably it
     * can be refactored / decomposed into smaller methods.
     *
     * @param {Array<HTMLElement>} referenceEls
     * @returns {undefined}
     */
    _toggleOnScroll(referenceEls) {
        if (this.animating) {
            return;
        }

        const scrollTop = Math.ceil(browser.scrollTop());
        const downwardScroll = scrollTop > this.lastScrollTop;
        const headerHeight = this.headerHeight - this.headerOffset;

        // Hide if any of the reference elements are visible within the viewport
        const shouldHide = referenceEls.some((referenceEl) => {
            const referenceElHeight = referenceEl.getBoundingClientRect().height;
            const { top, bottom } = referenceEl.getBoundingClientRect();
            const bottomEdge = downwardScroll
                ? window.innerHeight
                : window.innerHeight + referenceElHeight;
            const pastViewportTopEdge = (downwardScroll ? top : bottom) > 0;
            const beforeViewportBottomEdge = (downwardScroll ? top : bottom) < bottomEdge;
            const notOverlappedByHeader = bottom > headerHeight;

            // Is visible in viewport if below top edge, above bottom edge, and not obscured by the header
            return pastViewportTopEdge && notOverlappedByHeader && beforeViewportBottomEdge;
        });

        this.el.classList.toggle(states.FADE, shouldHide);
        this.lastScrollTop = scrollTop;
    },

    /**
     * Scrolls the viewport so that the Buy Options are centered within it.
     *
     * @returns {undefined}
     */
    _scrollToBuyOptions() {
        const buyOptionsEl = document.body.querySelector(selectors.BUY_OPTIONS);
        const buyOptionsOffset = buyOptionsEl ? buyOptionsEl.getBoundingClientRect().top : 0;
        const buyOptsVerticallyCentered =
            // We use a magic number (30) to try to get the size picker always visible too
            // when scrolling the viewport to the Buy Options section.
            buyOptionsOffset + window.pageYOffset - window.innerHeight / 2 - sizePickerOffset;

        analytics.event(
            "buy_area",
            "sticky_button_click",
            buyOptionsOffset < 0 ? "below_buy_options" : "above_buy_options"
        );

        browser.scrollTop(buyOptsVerticallyCentered, 300);
    },
});
